import { Component, OnInit, inject } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MainLayoutComponent } from '../../../_layouts/main-layout/main-layout.component';
import { Customer, CustomerOverview, CustomerPage } from '../../../_interfaces/customer';
import { UserCreateInput, UserUpdateInput } from '../../../_interfaces/users';
import { UserService } from '../../../_services/users.service';
import { OrderService } from '../../../_services/order.service';
import { AlertService } from '../../../_services/alert.service';
import { userRoles, userTypes } from '../../../../Shared/_constants/constants';
import { SetPasswordComponent } from '../../../../Shared/_components/set-password/set-password.component';
import { RecordNavComponent, confirmLeave } from '../../../../Shared/_components/record-nav/record-nav.component';
import type { CustomerListState } from '../customers.component';

const PHONE = /^\+?[0-9 ()-]{7,20}$/;
const UK_VAT = /^GB(\d{3} \d{4} \d{2}|\d{9}|\d{9} \d{3}|GD([0-4][0-9]{2})|HA([5-9][0-9]{2}))$/;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Create (/customers/create) or edit (/customers/edit/:id) a customer:
// their details, membership tier, account status and password, with their
// orders and addresses alongside. Previous / Next follow the Customers list
// (its search, filters, sort and page come along in the address).
@Component({
    selector: 'app-customer-edit',
    imports: [CommonModule, FormsModule, RouterLink, MainLayoutComponent, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, SetPasswordComponent, RecordNavComponent],
    templateUrl: './customer-edit.component.html'
})
export class CustomerEditComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private userService = inject(UserService);
  private orderService = inject(OrderService);
  private alertService = inject(AlertService);

  private readonly roles = String(this.userService.isLoggedIn()?.userrole ?? '').split(',').map((r: string) => r.trim());
  // Membership tier and account on/off: admins, sales admins and super admins (the server checks too).
  readonly canManage = ['ADMIN', 'SALEADMIN', 'S-ADMIN'].some((r) => this.roles.includes(r));

  readonly tiers = [
    { key: 'NONMEMBER', label: 'Non member', hint: 'Retail customer', icon: 'person' },
    { key: 'BRONZE', label: 'Bronze', hint: 'Trade account', icon: 'workspace_premium' },
    { key: 'SILVER', label: 'Silver', hint: 'Trade account', icon: 'workspace_premium' },
    { key: 'GOLD', label: 'Gold', hint: 'Trade account', icon: 'workspace_premium' },
    { key: 'PLATINUM', label: 'Platinum', hint: 'Trade account', icon: 'diamond' },
  ];

  customerId = 0;
  listState: CustomerListState = {};
  loading = true;
  notFound = false;
  detail: CustomerOverview | null = null;
  saving = false;
  tried = false;

  // ---- form ----
  email = '';
  firstname = '';
  lastname = '';
  displayname = '';
  phone = '';
  vatnumber = '';
  usertype = 'NONMEMBER';
  isActive = true;
  private loaded = '';

  // ---- previous / next (the list's pages, fetched as needed) ----
  private pages = new Map<number, Customer[]>();
  private navPage = 0;
  private pageSize = 10;
  navTotal = 0;

  get isEdit(): boolean {
    return this.customerId > 0;
  }

  ngOnInit(): void {
    this.customerId = Number(this.route.snapshot.paramMap.get('id')) || 0;
    const qp = this.route.snapshot.queryParamMap;
    for (const k of ['role', 'q', 'tier', 'activity', 'sort', 'page', 'size'] as const) {
      const v = qp.get(k);
      if (v !== null) this.listState[k] = v;
    }
    this.navPage = Number(this.listState.page) || 0;
    this.pageSize = Number(this.listState.size) || 10;
    if (this.isEdit) {
      this.loadCustomer();
      this.loadNavPage(this.navPage, true);
    } else {
      this.loading = false;
      this.loaded = this.snapshot;
    }
  }

  private loadCustomer(): void {
    this.loading = true;
    this.userService.getCustomerOverview(this.customerId).subscribe({
      next: (d: CustomerOverview) => {
        this.detail = d;
        this.email = d.useremail ?? '';
        this.firstname = d.firstname ?? '';
        this.lastname = d.lastname ?? '';
        this.displayname = d.displayname ?? '';
        this.phone = d.phone ?? '';
        this.vatnumber = d.vatnumber ?? '';
        this.usertype = d.usertype || 'NONMEMBER';
        this.isActive = !!d.isActive;
        this.loaded = this.snapshot;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.notFound = true;
      },
    });
  }

  // ---- previous / next ----
  private loadNavPage(page: number, findNeighbours = false): void {
    if (page < 0 || this.pages.has(page)) return;
    const s = this.listState;
    this.userService
      .getCustomersPaged(
        { pageSize: this.pageSize, pageIndex: page, userType: s.tier || 'ALL', keywordSearch: s.q || '', activity: s.activity || 'ALL', sort: s.sort || 'NEWEST' },
        s.role === 'trader' ? userRoles.TRADER : userRoles.CUSTOMER,
      )
      .subscribe({
        next: (r: CustomerPage) => {
          this.pages.set(page, r?.users ?? []);
          this.navTotal = r?.count ?? 0;
          if (!findNeighbours) return;
          // At either end of the page, have the neighbouring page ready.
          const rows = r?.users ?? [];
          const i = rows.findIndex((c) => c.userid === this.customerId);
          if (i === 0) this.loadNavPage(page - 1);
          if (i === rows.length - 1 && (page + 1) * this.pageSize < this.navTotal) this.loadNavPage(page + 1);
        },
        error: () => {},
      });
  }

  private get navRows(): Customer[] {
    return this.pages.get(this.navPage) ?? [];
  }

  private get navIndex(): number {
    return this.navRows.findIndex((c) => c.userid === this.customerId);
  }

  get position(): number {
    return this.navIndex >= 0 ? this.navPage * this.pageSize + this.navIndex : -1;
  }

  get prev(): { c: Customer; page: number } | null {
    const i = this.navIndex;
    if (i < 0) return null;
    if (i > 0) return { c: this.navRows[i - 1], page: this.navPage };
    const before = this.pages.get(this.navPage - 1);
    return before?.length ? { c: before[before.length - 1], page: this.navPage - 1 } : null;
  }

  get next(): { c: Customer; page: number } | null {
    const i = this.navIndex;
    if (i < 0) return null;
    if (i < this.navRows.length - 1) return { c: this.navRows[i + 1], page: this.navPage };
    const after = this.pages.get(this.navPage + 1);
    return after?.length ? { c: after[0], page: this.navPage + 1 } : null;
  }

  nameOf(c: { firstname?: string; lastname?: string; useremail?: string } | null | undefined): string | null {
    if (!c) return null;
    return `${c.firstname ?? ''} ${c.lastname ?? ''}`.replace(/\s+/g, ' ').trim() || c.useremail || 'Unnamed';
  }

  goTo(target: { c: Customer; page: number } | null): void {
    if (!target || this.saving) return;
    confirmLeave(this.dialog, this.dirty, this.nameOf(this.detail) ?? '', this.nameOf(target.c)!).subscribe((ok) => {
      if (!ok) return;
      const state = { ...this.listState, page: target.page ? String(target.page) : undefined };
      this.router.navigate(['/customers/edit', target.c.userid], { queryParams: state });
    });
  }

  backToList(): void {
    const go = () =>
      this.router.navigate(['/customers'], { queryParams: { ...this.listState, page: this.navPage ? String(this.navPage) : undefined, selected: this.isEdit ? this.customerId : undefined } });
    confirmLeave(this.dialog, this.dirty, this.nameOf(this.detail) ?? 'this customer', 'the list').subscribe((ok) => ok && go());
  }

  // ---- form state ----
  private get snapshot(): string {
    return JSON.stringify([this.email, this.firstname, this.lastname, this.displayname, this.phone, this.vatnumber, this.usertype, this.isActive]);
  }

  get dirty(): boolean {
    return !this.loading && this.snapshot !== this.loaded;
  }

  get errors(): Record<string, string> {
    const e: Record<string, string> = {};
    const len = (v: string, min: number, max: number) => v.trim().length >= min && v.trim().length <= max;
    if (!this.isEdit) {
      if (!this.email.trim()) e['email'] = 'Email is required';
      else if (!EMAIL.test(this.email.trim())) e['email'] = 'That is not a valid email address';
    }
    if (!this.firstname.trim()) e['firstname'] = 'First name is required';
    else if (!len(this.firstname, 2, 200)) e['firstname'] = 'Between 2 and 200 characters';
    if (!this.lastname.trim()) e['lastname'] = 'Last name is required';
    else if (!len(this.lastname, 2, 200)) e['lastname'] = 'Between 2 and 200 characters';
    if (this.displayname.trim() && !len(this.displayname, 2, 300)) e['displayname'] = 'Between 2 and 300 characters';
    if (this.phone.trim() && !PHONE.test(this.phone.trim())) e['phone'] = 'Use digits, spaces, + ( ) or - (7 to 20 characters)';
    if (this.vatnumber.trim() && !UK_VAT.test(this.vatnumber.trim().toUpperCase())) e['vatnumber'] = 'Not a valid UK VAT number (e.g. GB123456789)';
    return e;
  }

  get firstError(): string | null {
    return Object.values(this.errors)[0] ?? null;
  }

  show(field: string): string | null {
    return this.tried ? this.errors[field] ?? null : null;
  }

  get currentName(): string {
    return this.nameOf({ firstname: this.firstname, lastname: this.lastname, useremail: this.email }) ?? '';
  }

  get isTrade(): boolean {
    return this.usertype !== 'NONMEMBER';
  }

  discard(): void {
    if (this.isEdit) this.loadCustomer();
    else {
      this.email = this.firstname = this.lastname = this.displayname = this.phone = this.vatnumber = '';
      this.usertype = 'NONMEMBER';
      this.tried = false;
    }
  }

  save(): void {
    this.tried = true;
    if (this.firstError || this.saving) return;
    this.saving = true;
    const opt = (v: string) => (v.trim() ? v.trim() : undefined);
    if (this.isEdit) {
      const input: UserUpdateInput = {
        id: this.customerId,
        firstname: this.firstname.trim(),
        lastname: this.lastname.trim(),
        displayname: opt(this.displayname),
        vatnumber: opt(this.vatnumber.toUpperCase()),
        phone: opt(this.phone),
      };
      if (this.canManage) {
        input.usertype = this.usertype as userTypes;
        input.userrole = this.isTrade ? userRoles.TRADER : userRoles.CUSTOMER;
        if (this.detail && this.isActive !== !!this.detail.isActive) input.isActive = this.isActive;
      }
      this.userService.updateUser(input).subscribe({
        next: () => {
          this.saving = false;
          this.alertService.alert(`${this.currentName || 'Customer'} updated`, 'success');
          this.tried = false;
          this.loadCustomer();
        },
        error: (error) => {
          this.saving = false;
          this.alertService.alert(error?.error?.message || 'Could not save the customer', 'error');
        },
      });
    } else {
      const input: UserCreateInput = {
        useremail: this.email.trim().toLowerCase(),
        userrole: userRoles.CUSTOMER,
        usertype: userTypes.NONMEMBER,
        firstname: this.firstname.trim(),
        lastname: this.lastname.trim(),
        displayname: opt(this.displayname),
        vatnumber: opt(this.vatnumber.toUpperCase()),
        phone: opt(this.phone),
        subscribe: true,
      };
      this.userService.createUserWithRole(input).subscribe({
        next: (created: any) => {
          this.saving = false;
          this.alertService.alert('Customer created', 'success');
          this.loaded = this.snapshot;
          const id = Number(created?.userid ?? created?.id);
          if (id > 0) this.router.navigate(['/customers/edit', id], { queryParams: { ...this.listState, sort: undefined, page: undefined } });
          else this.router.navigate(['/customers'], { queryParams: { sort: 'NEWEST' } });
        },
        error: (error) => {
          this.saving = false;
          this.alertService.alert(error?.error?.message || 'Could not create the customer', 'error');
        },
      });
    }
  }

  // ---- side panel ----
  viewOrders(): void {
    if (!this.detail) return;
    this.orderService.setSearchParams({ email: this.detail.useremail, dtFrom: '2020-01-01', dtTo: formatDate(new Date(), 'yyyy-MM-dd', 'en'), status: 'ALL' });
    this.router.navigate(['/sales']);
  }

  openOrder(orderNo: string): void {
    this.router.navigate(['/sales', orderNo]);
  }

  initials(): string {
    const a = (this.firstname || this.email || '?').trim()[0] ?? '?';
    const b = (this.lastname || '').trim()[0] ?? '';
    return (a + b).toUpperCase();
  }

  statusClass(status: string): string {
    switch (status) {
      case 'COMPLETED': return 'bg-emerald-100 text-emerald-800';
      case 'PROCESSING': return 'bg-sky-100 text-sky-800';
      case 'PENDING': case 'PENDING COLLECTION': return 'bg-amber-100 text-amber-800';
      case 'CANCELLED': case 'VOID': case 'FAILED': return 'bg-neutral-100 text-neutral-500';
      case 'REFUNDED': case 'PARTIALLY REFUNDED': return 'bg-rose-100 text-rose-700';
      default: return 'bg-orange-100 text-orange-800';
    }
  }

  mapsUrl(a: { address1?: string; address2?: string; city?: string; postCode?: string; country?: string }): string {
    const q = [a.address1, a.address2, a.city, a.postCode, a.country].filter(Boolean).join(', ');
    return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(q);
  }
}
